import React, { Component } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Helmet } from 'react-helmet';
import './moderation.css';
import './timeline.css'; // for ConfirmDialog styles
import ConfirmDialog from './ConfirmDialog';

const BLOCK_PAGE   = 8;
const RESTRICT_PAGE = 8;
const LOG_PAGE     = 15;

const ACTION_META = {
  venue_block:      { label: 'Blocked user',        icon: '🚫', cls: 'block' },
  venue_unblock:    { label: 'Unblocked user',       icon: '✅', cls: 'unblock' },
  venue_restrict:   { label: 'Restricted user',      icon: '⏱️', cls: 'restrict' },
  venue_unrestrict: { label: 'Removed restriction',  icon: '🔓', cls: 'unrestrict' },
};

function getActionMeta(type) {
  return ACTION_META[type] || { label: type, icon: '📋', cls: 'other' };
}

function paginate(arr, page, size) {
  return arr.slice(page * size, (page + 1) * size);
}

function Pagination({ total, page, size, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / size));
  if (totalPages <= 1) return null;
  return (
    <div className="mod-pagination">
      <button
        className="mod-page-btn"
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
      >← Prev</button>

      {Array.from({ length: totalPages }, (_, i) => {
        const show = totalPages <= 7 || i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 1;
        const ellipsis = Math.abs(i - page) === 2 && totalPages > 7;
        if (ellipsis) return <span key={i} className="mod-page-ellipsis">…</span>;
        if (!show) return null;
        return (
          <button
            key={i}
            className={`mod-page-btn${page === i ? ' current' : ''}`}
            onClick={() => onChange(i)}
          >{i + 1}</button>
        );
      })}

      <button
        className="mod-page-btn"
        disabled={page === totalPages - 1}
        onClick={() => onChange(page + 1)}
      >Next →</button>
    </div>
  );
}

class Moderation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      blocks: [],
      restrictions: [],
      log: [],
      userNames: {},
      userEmails: {},
      loading: true,
      barInfo: null,
      confirm: null,
      activeTab: 'blocked',
      blockSearch: '',
      blocksPage: 0,
      restrictSearch: '',
      restrictionsPage: 0,
      logSearch: '',
      logPage: 0,
    };
  }

  componentDidMount() {
    const data = localStorage.getItem('info');
    const barInfo = data ? JSON.parse(data) : null;
    this.setState({ barInfo }, () => {
      if (barInfo) this.load();
    });
  }

  load() {
    const { barInfo } = this.state;
    const db = firebase.firestore();
    Promise.all([
      db.collection('venueBlocks').where('venueId', '==', barInfo.id).get(),
      db.collection('venueRestrictions').where('venueId', '==', barInfo.id).get(),
      db.collection('moderationLog').where('venueId', '==', barInfo.id)
        .orderBy('createdAt', 'desc').limit(100).get(),
    ]).then(([bSnap, rSnap, lSnap]) => {
      const blocks       = bSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const restrictions = rSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const log          = lSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      this.setState({ blocks, restrictions, log, loading: false });
      const uids = [...new Set([
        ...blocks.map(b => b.blockedUid),
        ...restrictions.map(r => r.restrictedUid),
        ...log.map(l => l.targetUid),
      ].filter(Boolean))];
      uids.forEach(uid => this.loadUser(uid));
    }).catch(() => this.setState({ loading: false }));
  }

  loadUser(uid) {
    firebase.firestore().collection('users').doc(uid).get().then(snap => {
      if (!snap.exists) return;
      const d = snap.data();
      this.setState(prev => ({
        userNames: { ...prev.userNames, [uid]: d.name || d.username || d.email || uid },
        userEmails: { ...prev.userEmails, [uid]: d.email || null },
      }));
    }).catch(() => {});
  }

  unblock(id, blockedUid) {
    this.setState({ confirm: { type: 'unblock', id, targetUid: blockedUid } });
  }

  doUnblock(id, targetUid) {
    this.setState({ confirm: null });
    const { barInfo } = this.state;
    const db = firebase.firestore();
    const batch = db.batch();
    batch.delete(db.collection('venueBlocks').doc(id));
    batch.set(db.collection('moderationLog').doc(), {
      type: 'venue_unblock',
      venueId: barInfo.id,
      targetUid,
      actorUid: firebase.auth().currentUser?.uid || '',
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
    batch.commit()
      .then(() => this.setState(prev => ({ blocks: prev.blocks.filter(b => b.id !== id) })))
      .catch(() => alert('Could not unblock. Please try again.'));
  }

  unrestrict(id, restrictedUid) {
    this.setState({ confirm: { type: 'unrestrict', id, targetUid: restrictedUid } });
  }

  doUnrestrict(id, targetUid) {
    this.setState({ confirm: null });
    const { barInfo } = this.state;
    const db = firebase.firestore();
    const batch = db.batch();
    batch.delete(db.collection('venueRestrictions').doc(id));
    batch.set(db.collection('moderationLog').doc(), {
      type: 'venue_unrestrict',
      venueId: barInfo.id,
      targetUid,
      actorUid: firebase.auth().currentUser?.uid || '',
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
    batch.commit()
      .then(() => this.setState(prev => ({ restrictions: prev.restrictions.filter(r => r.id !== id) })))
      .catch(() => alert('Could not remove restriction. Please try again.'));
  }

  fmtDate(ts) {
    if (!ts) return '';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  }

  fmtUntil(ts) {
    if (!ts) return { label: '—', cls: 'expired' };
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
      const now = new Date();
      if (d < now) return { label: 'Expired', cls: 'expired' };
      const diffH = Math.round((d - now) / 3600000);
      if (diffH < 6)  return { label: `${diffH}h remaining`, cls: 'soon' };
      if (diffH < 24) return { label: `${diffH}h remaining`, cls: 'active' };
      return { label: `${Math.round(diffH / 24)}d remaining`, cls: 'active' };
    } catch { return { label: '—', cls: 'expired' }; }
  }

  getInitials(name) {
    if (!name || name === '—') return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  filteredBlocks() {
    const { blocks, blockSearch, userNames, userEmails } = this.state;
    const q = blockSearch.toLowerCase().trim();
    if (!q) return blocks;
    return blocks.filter(b =>
      (userNames[b.blockedUid] || '').toLowerCase().includes(q) ||
      (userEmails[b.blockedUid] || '').toLowerCase().includes(q) ||
      (b.blockedUid || '').toLowerCase().includes(q)
    );
  }

  filteredRestrictions() {
    const { restrictions, restrictSearch, userNames, userEmails } = this.state;
    const q = restrictSearch.toLowerCase().trim();
    if (!q) return restrictions;
    return restrictions.filter(r =>
      (userNames[r.restrictedUid] || '').toLowerCase().includes(q) ||
      (userEmails[r.restrictedUid] || '').toLowerCase().includes(q) ||
      (r.restrictedUid || '').toLowerCase().includes(q)
    );
  }

  filteredLog() {
    const { log, logSearch, userNames, userEmails } = this.state;
    const q = logSearch.toLowerCase().trim();
    if (!q) return log;
    return log.filter(e =>
      (userNames[e.targetUid] || '').toLowerCase().includes(q) ||
      (userEmails[e.targetUid] || '').toLowerCase().includes(q) ||
      (getActionMeta(e.type).label).toLowerCase().includes(q) ||
      (e.type || '').toLowerCase().includes(q)
    );
  }

  render() {
    const {
      blocks, restrictions, log,
      userNames, userEmails, loading, confirm,
      activeTab,
      blockSearch, blocksPage,
      restrictSearch, restrictionsPage,
      logSearch, logPage,
    } = this.state;

    const filtBlocks = this.filteredBlocks();
    const filtRestrict = this.filteredRestrictions();
    const filtLog = this.filteredLog();

    const confirmConfig = confirm && {
      unblock: {
        title: 'Unblock user',
        message: `Allow ${userNames[confirm.targetUid] || 'this user'} to post on your venue timeline again?`,
        confirmLabel: 'Unblock',
        variant: 'primary',
        onConfirm: () => this.doUnblock(confirm.id, confirm.targetUid),
      },
      unrestrict: {
        title: 'Remove restriction',
        message: `Remove the posting restriction for ${userNames[confirm.targetUid] || 'this user'}?`,
        confirmLabel: 'Remove',
        variant: 'primary',
        onConfirm: () => this.doUnrestrict(confirm.id, confirm.targetUid),
      },
    }[confirm?.type];

    return (
      <div className="mod-page">
        <Helmet>
          <style>{'body { background-color: #F7F7F7 !important; }'}</style>
        </Helmet>

        {/* ── Header ── */}
        <div className="mod-header">
          <p className="mod-title">Moderation</p>
          {!loading && (
            <div className="mod-header-stats">
              <span className="mod-stat-chip blocked">
                <span className="mod-stat-dot" />
                {blocks.length} Blocked
              </span>
              <span className="mod-stat-chip restrict">
                <span className="mod-stat-dot" />
                {restrictions.length} Restricted
              </span>
              <span className="mod-stat-chip log-chip">
                <span className="mod-stat-dot" />
                {log.length} Log entries
              </span>
            </div>
          )}
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="mod-loading">
            <div className="mod-spinner" />
            <p style={{ color: '#888', margin: 0 }}>Loading moderation data…</p>
          </div>
        )}

        {/* ── Tabs ── */}
        {!loading && (
          <div className="mod-tabs">
            <button
              className={`mod-tab-btn${activeTab === 'blocked' ? ' active' : ''}`}
              onClick={() => this.setState({ activeTab: 'blocked' })}
            >
              <span className="mod-tab-icon">🚫</span>Blocked Users
              <span className="mod-tab-count">{blocks.length}</span>
            </button>
            <button
              className={`mod-tab-btn${activeTab === 'restricted' ? ' active' : ''}`}
              onClick={() => this.setState({ activeTab: 'restricted' })}
            >
              <span className="mod-tab-icon">⏱</span>Restricted Users
              <span className="mod-tab-count">{restrictions.length}</span>
            </button>
            <button
              className={`mod-tab-btn${activeTab === 'log' ? ' active' : ''}`}
              onClick={() => this.setState({ activeTab: 'log' })}
            >
              <span className="mod-tab-icon">📋</span>Moderation Log
              <span className="mod-tab-count">{log.length}</span>
            </button>
          </div>
        )}

        {!loading && (
          <>
            {/* ════ Blocked Users ════ */}
            {activeTab === 'blocked' && <div className="mod-section">
              <div className="mod-section-head">
                <div className="mod-section-title-row">
                  <span className="mod-section-icon">🚫</span>
                  <p className="mod-section-title">Blocked Users</p>
                  <span className="mod-count-badge">{blocks.length}</span>
                </div>
                {blocks.length > 0 && (
                  <div className="mod-section-search">
                    <span className="mod-search-icon">🔍</span>
                    <input
                      className="mod-search-input"
                      placeholder="Search by name or email…"
                      value={blockSearch}
                      onChange={e => this.setState({ blockSearch: e.target.value, blocksPage: 0 })}
                    />
                  </div>
                )}
              </div>

              {blocks.length === 0 ? (
                <p className="mod-empty-row">No blocked users. 🎉</p>
              ) : filtBlocks.length === 0 ? (
                <p className="mod-empty-row">No users match your search.</p>
              ) : (
                <>
                  <div className="mod-user-list">
                    {paginate(filtBlocks, blocksPage, BLOCK_PAGE).map(b => {
                      const name = userNames[b.blockedUid] || '—';
                      const email = userEmails[b.blockedUid] || null;
                      return (
                        <div key={b.id} className="mod-user-row">
                          <div className="mod-avatar">{this.getInitials(name)}</div>
                          <div className="mod-user-info">
                            <span className="mod-user-name">{name}</span>
                            {email && <span className="mod-user-email">{email}</span>}
                            <span className="mod-user-meta">Blocked {this.fmtDate(b.blockedAt)}</span>
                          </div>
                          <button
                            className="mod-action-btn unblock"
                            onClick={() => this.unblock(b.id, b.blockedUid)}
                          >
                            Unblock
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <Pagination
                    total={filtBlocks.length}
                    page={blocksPage}
                    size={BLOCK_PAGE}
                    onChange={p => this.setState({ blocksPage: p })}
                  />
                </>
              )}
            </div>}

            {/* ════ Restricted Users ════ */}
            {activeTab === 'restricted' && <div className="mod-section">
              <div className="mod-section-head">
                <div className="mod-section-title-row">
                  <span className="mod-section-icon">⏱️</span>
                  <p className="mod-section-title">Restricted Users</p>
                  <span className="mod-count-badge">{restrictions.length}</span>
                </div>
                {restrictions.length > 0 && (
                  <div className="mod-section-search">
                    <span className="mod-search-icon">🔍</span>
                    <input
                      className="mod-search-input"
                      placeholder="Search by name or email…"
                      value={restrictSearch}
                      onChange={e => this.setState({ restrictSearch: e.target.value, restrictionsPage: 0 })}
                    />
                  </div>
                )}
              </div>

              {restrictions.length === 0 ? (
                <p className="mod-empty-row">No restricted users.</p>
              ) : filtRestrict.length === 0 ? (
                <p className="mod-empty-row">No users match your search.</p>
              ) : (
                <>
                  <div className="mod-user-list">
                    {paginate(filtRestrict, restrictionsPage, RESTRICT_PAGE).map(r => {
                      const name = userNames[r.restrictedUid] || '—';
                      const email = userEmails[r.restrictedUid] || null;
                      const { label, cls } = this.fmtUntil(r.until);
                      return (
                        <div key={r.id} className="mod-user-row">
                          <div className="mod-avatar">{this.getInitials(name)}</div>
                          <div className="mod-user-info">
                            <span className="mod-user-name">{name}</span>
                            {email && <span className="mod-user-email">{email}</span>}
                            <span className="mod-user-meta">Restricted {this.fmtDate(r.restrictedAt)}</span>
                          </div>
                          <span className={`mod-badge ${cls}`}>{label}</span>
                          <button
                            className="mod-action-btn unrestrict"
                            onClick={() => this.unrestrict(r.id, r.restrictedUid)}
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <Pagination
                    total={filtRestrict.length}
                    page={restrictionsPage}
                    size={RESTRICT_PAGE}
                    onChange={p => this.setState({ restrictionsPage: p })}
                  />
                </>
              )}
            </div>}

            {/* ════ Moderation Log ════ */}
            {activeTab === 'log' && <div className="mod-section">
              <div className="mod-section-head">
                <div className="mod-section-title-row">
                  <span className="mod-section-icon">📋</span>
                  <p className="mod-section-title">Moderation Log</p>
                  <span className="mod-count-badge">{log.length}</span>
                </div>
                {log.length > 0 && (
                  <div className="mod-section-search">
                    <span className="mod-search-icon">🔍</span>
                    <input
                      className="mod-search-input"
                      placeholder="Search by user or action…"
                      value={logSearch}
                      onChange={e => this.setState({ logSearch: e.target.value, logPage: 0 })}
                    />
                  </div>
                )}
              </div>

              {log.length === 0 ? (
                <p className="mod-empty-row">No moderation actions taken yet.</p>
              ) : filtLog.length === 0 ? (
                <p className="mod-empty-row">No entries match your search.</p>
              ) : (
                <>
                  <div className="mod-log-list">
                    {paginate(filtLog, logPage, LOG_PAGE).map(entry => {
                      const meta = getActionMeta(entry.type);
                      const userName = userNames[entry.targetUid] || entry.targetUid || '—';
                      const userEmail = userEmails[entry.targetUid] || null;
                      return (
                        <div key={entry.id} className="mod-log-row">
                          <div className={`mod-log-icon ${meta.cls}`}>{meta.icon}</div>
                          <div className="mod-log-info">
                            <span className="mod-log-action">{meta.label}</span>
                            <span className="mod-log-user">{userName}</span>
                            {userEmail && <span className="mod-log-email">{userEmail}</span>}
                          </div>
                          <span className="mod-log-date">{this.fmtDate(entry.createdAt)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <Pagination
                    total={filtLog.length}
                    page={logPage}
                    size={LOG_PAGE}
                    onChange={p => this.setState({ logPage: p })}
                  />
                </>
              )}
            </div>}
          </>
        )}

        {confirm && confirmConfig && (
          <ConfirmDialog
            open
            title={confirmConfig.title}
            message={confirmConfig.message}
            confirmLabel={confirmConfig.confirmLabel}
            confirmVariant={confirmConfig.variant}
            onConfirm={confirmConfig.onConfirm}
            onCancel={() => this.setState({ confirm: null })}
          />
        )}
      </div>
    );
  }
}

export default Moderation;
